import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SidebarLogo, SidebarNav, SidebarPortalLink } from './Sidebar';

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Slide-over navigation for screens below the `md` breakpoint. The desktop
 * Sidebar is hidden there; this drawer reuses its logo/nav/link pieces.
 */
export function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden animate-fade-in"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel — always mounted so the slide transition runs */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw]',
          'bg-[#09193b] dark:bg-[#050d1d]',
          'flex flex-col transition-[transform,visibility] duration-300 md:hidden',
          'pb-[env(safe-area-inset-bottom)]',
          // `invisible` while closed keeps off-screen links out of tab order;
          // transitioning visibility lets the slide-out finish before hiding
          open ? 'visible translate-x-0' : 'invisible -translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        aria-hidden={!open}
      >
        {/* Logo + close */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <SidebarLogo collapsed={false} />
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center min-h-11 min-w-11 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close navigation"
            tabIndex={open ? 0 : -1}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto">
          <SidebarNav collapsed={false} touch />
        </nav>

        {/* External Portal Link */}
        <div className="px-3 py-2 border-t border-white/10">
          <SidebarPortalLink collapsed={false} touch />
        </div>
      </div>
    </>
  );
}
